/**
 * Age is derived from a birth month and year — never stored as a number.
 *
 * Two reasons, one privacy and one correctness.
 *
 * Privacy: month and year are enough to run the allowance formula and to time
 * the raise, and they are meaningfully less identifying than a full date of
 * birth. A children's product should collect the least that works.
 *
 * Correctness: a stored age silently goes stale, and the failure is invisible —
 * the allowance simply stops rising and nobody notices until a ten-year-old is
 * still being paid like an eight-year-old. Derived age cannot drift.
 *
 * The raise lands on the first of the birth month. That is a deliberate
 * simplification of not knowing the day, and it is also better product: the
 * raise arrives at a period boundary instead of mid-week, so no period is ever
 * priced at two different ages.
 */

import { parseDateOnly } from './periods.js';

export function ageAt(date, { birthMonth, birthYear }) {
  const { year, month } = parseDateOnly(date);
  const years = year - birthYear;
  return month < birthMonth ? years - 1 : years;
}

/** The date this child's next raise lands on, given a date to count from. */
export function nextRaiseDate(date, { birthMonth }) {
  const { year, month } = parseDateOnly(date);
  const raiseYear = month < birthMonth ? year : year + 1;
  return `${raiseYear}-${String(birthMonth).padStart(2, '0')}-01`;
}

export const MONTH_NAMES = Object.freeze([
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]);
